import classNames from "classnames";
import React from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import iconXClose from "../../../../img/icons/x-close.svg";
import {
    IReviewsProductID,
    IReviewsProductTypeID,
    IReviewsProductVariantID,
    isoReviewsProductID,
    isoReviewsProductTypeID,
    isoReviewsProductVariantID,
} from "../../../models/nominals";
import {
    IReviewQueryFacets,
    IReviewsProduct,
    ISearchFacet,
    ISearchFacetOption,
    ISearchFacetSubOption,
} from "../../../models/reviews";
import { notEmpty } from "../../../utils/functional";
import { parseFacetValues } from "../../../utils/reviews";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { defaultState } from "../defaults";
import { clearFilters, updateFilterOptionValue } from "../reducers";

import styles from "./AppliedFilters.module.scss";

type FacetOptionDictionary = {
    [key: number]: ISearchFacetOption;
};

type VariantOption = {
    product_id: number;
    option: ISearchFacetSubOption;
};

type VariantDictionary = {
    [key: number]: VariantOption;
};

type ProductTypeDictionary = {
    [key: number]: string;
};

type LookupTables = {
    products: FacetOptionDictionary;
    variants: VariantDictionary;
    sources: FacetOptionDictionary;
    productTypes: ProductTypeDictionary;
};

const buildLookupTables = (
    facets: ReadonlyArray<ISearchFacet>,
    products: ReadonlyArray<IReviewsProduct>,
): LookupTables => {
    const lookupProducts: FacetOptionDictionary = {};
    const lookupVariants: VariantDictionary = {};
    const lookupSources: FacetOptionDictionary = {};
    const lookupProductTypes: ProductTypeDictionary = {};

    // Construct a lookup table for Product and Variant IDs
    const productFacet = facets.find((facet) => {
        return facet.type === "product_id";
    });
    if (productFacet) {
        productFacet.options.forEach((option) => {
            lookupProducts[option.option_id] = option;

            option.sub_options.forEach((sub_option) => {
                const variantOption: VariantOption = {
                    product_id: option.option_id,
                    option: sub_option,
                };
                lookupVariants[sub_option.option_id] = variantOption;
            });
        });
    }

    // Construct lookup table for sources
    const sourceFacet = facets.find((facet) => {
        return facet.type === "source_id";
    });
    if (sourceFacet) {
        sourceFacet.options.forEach((option) => {
            lookupSources[option.option_id] = option;
        });
    }

    // Construct lookup table for ProductType IDs
    products.map((product) => {
        if (product.product_type && product.product_type_name) {
            lookupProductTypes[
                isoReviewsProductTypeID.unwrap(product.product_type)
            ] = product.product_type_name;
        }
    });

    return {
        products: lookupProducts,
        variants: lookupVariants,
        sources: lookupSources,
        productTypes: lookupProductTypes,
    };
};

const buildFilterTag = (
    filterType: IReviewQueryFacets,
    uniqueID: number,
    filterText: string,
    dispatch: ReturnType<typeof useAppDispatch>,
) => {
    return (
        <div className="tag" key={uniqueID}>
            <span
                className="text"
                dangerouslySetInnerHTML={{ __html: filterText }}
            ></span>
            <button
                className="remove-button"
                onClick={() => {
                    dispatch(
                        updateFilterOptionValue({
                            facetType: filterType,
                            optionID: uniqueID,
                            isSelected: false,
                        }),
                    );
                }}
            >
                <SVG className="icon" src={iconXClose} role="img" />
            </button>
        </div>
    );
};

const buildProductTypeTags = (
    productTypeIDs: readonly IReviewsProductTypeID[],
    productTypes: ProductTypeDictionary,
    dispatch: ReturnType<typeof useAppDispatch>,
) => {
    return productTypeIDs.filter(notEmpty).map((productTypeID) => {
        const productType =
            productTypes[isoReviewsProductTypeID.unwrap(productTypeID)];
        if (productType) {
            return buildFilterTag(
                "product_type_id",
                isoReviewsProductTypeID.unwrap(productTypeID),
                productType,
                dispatch,
            );
        }
        return null;
    });
};

const buildProductFilterTags = (
    productIDs: IReviewsProductID[],
    products: FacetOptionDictionary,
    dispatch: ReturnType<typeof useAppDispatch>,
) => {
    return productIDs.filter(notEmpty).map((productID) => {
        const product = products[isoReviewsProductID.unwrap(productID)];
        if (product) {
            return buildFilterTag(
                "product_id",
                isoReviewsProductID.unwrap(productID),
                product.name.toString(),
                dispatch,
            );
        }
        return null;
    });
};

const buildProductVariantFilterTags = (
    variantIDs: IReviewsProductVariantID[],
    products: FacetOptionDictionary,
    variants: VariantDictionary,
    dispatch: ReturnType<typeof useAppDispatch>,
) => {
    return variantIDs.filter(notEmpty).map((variantID) => {
        const variant = variants[isoReviewsProductVariantID.unwrap(variantID)];
        if (variant) {
            const productName = products[variant.product_id].group_name;
            const filterText =
                `${productName} - ${variant.option.name}`.toString();
            return buildFilterTag(
                "product_variant_id",
                isoReviewsProductVariantID.unwrap(variantID),
                filterText,
                dispatch,
            );
        }
        return null;
    });
};

const buildRatingFilterTags = (
    ratings: number[],
    dispatch: ReturnType<typeof useAppDispatch>,
) => {
    return ratings.map((ratingValue) => {
        return buildFilterTag(
            "rating",
            ratingValue,
            `${ratingValue} Star rating`.toString(),
            dispatch,
        );
    });
};

const buildSourceFilterTags = (
    sourceIDs: number[],
    sources: FacetOptionDictionary,
    dispatch: ReturnType<typeof useAppDispatch>,
) => {
    return sourceIDs.map((sourceID) => {
        const source = sources[sourceID];
        if (source) {
            return buildFilterTag(
                "source_id",
                sourceID,
                sources[sourceID].name.toString(),
                dispatch,
            );
        }
        return null;
    });
};

export const AppliedFilters = (): React.ReactNode => {
    const products = useAppSelector((state) => {
        return (state.reviews || defaultState).data.products;
    });
    const productTypeIDs = useAppSelector((state) => {
        return (state.reviews || defaultState).ui.productTypeIDs;
    });
    const facets = useAppSelector((state) => {
        return (state.reviews || defaultState).data.facets;
    });
    const facetValues = useAppSelector((state) => {
        return (state.reviews || defaultState).ui.facetValues;
    });

    const dispatch = useAppDispatch();

    const [showFilterTags, setShowFilterTags] = React.useState(false);

    // Build lookup tables from facets and products
    const lookupTables = buildLookupTables(facets, products);

    // Parse facet values
    const productIDs = parseFacetValues(
        facetValues.product_id,
    ) as unknown as IReviewsProductID[];

    const variantIDs = parseFacetValues(
        facetValues.product_variant_id,
    ) as unknown as IReviewsProductVariantID[];

    const ratings = parseFacetValues(facetValues.rating)
        .reverse()
        .map((r) => parseInt(r, 10));

    const sourceIDs = parseFacetValues(facetValues.source_id).map((s) =>
        parseInt(s, 10),
    );

    const toggleFilterTags = (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        setShowFilterTags((prev) => !prev);
    };

    const totalAppliedFilters =
        productTypeIDs.length +
        productIDs.length +
        variantIDs.length +
        ratings.length +
        sourceIDs.length;

    const appliedFilterClasses = classNames({
        title: true,
        active: showFilterTags,
    });

    return (
        <>
            <div className={styles.appliedFilters}>
                <button
                    className={appliedFilterClasses}
                    onClick={toggleFilterTags}
                >
                    Applied Filters {`(${totalAppliedFilters})`}
                </button>
                <button
                    className="clear-button"
                    onClick={() => dispatch(clearFilters())}
                >
                    {t`Clear All`}
                </button>
            </div>
            {showFilterTags && (
                <div className={styles.filterTags}>
                    {buildProductTypeTags(
                        productTypeIDs,
                        lookupTables.productTypes,
                        dispatch,
                    )}
                    {buildProductFilterTags(
                        productIDs,
                        lookupTables.products,
                        dispatch,
                    )}
                    {buildProductVariantFilterTags(
                        variantIDs,
                        lookupTables.products,
                        lookupTables.variants,
                        dispatch,
                    )}
                    {buildRatingFilterTags(ratings, dispatch)}
                    {buildSourceFilterTags(
                        sourceIDs,
                        lookupTables.sources,
                        dispatch,
                    )}
                </div>
            )}
        </>
    );
};
